import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Live charter-seat scarcity counter.
 * Pulls GET /api/charter/stats and renders a progress bar + numbers.
 *
 * Props:
 *   variant: "banner" (default, full-width horizontal bar) | "compact" (small pill)
 *   className: optional extra classes for the outer wrapper
 */
export const CharterCounter = ({ variant = 'banner', className = '' }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/charter/stats`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setStats(data);
      } catch (_e) {
        /* ignore — non-critical UI element */
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  if (!stats) return null;

  const { cap, claimed, remaining, percent, is_soft_capped } = stats;
  const fmt = (n) => n.toLocaleString('en-US');
  const pctClamped = Math.max(Math.min(percent, 100), 2); // min 2% so the bar is visible when empty

  if (variant === 'compact') {
    return (
      <div
        className={`inline-flex items-center gap-2 rounded-full border border-amber-300 bg-white/90 px-3 py-1 text-xs font-medium text-amber-900 shadow-sm ${className}`}
        data-testid="charter-counter-compact"
      >
        <Sparkles className="h-3.5 w-3.5 text-amber-500" />
        <span>
          <span data-testid="charter-counter-claimed">{fmt(claimed)}</span>
          <span className="text-amber-700"> / {fmt(cap)} charter seats</span>
        </span>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 p-4 ${className}`}
      data-testid="charter-counter"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <Sparkles className="h-4 w-4 text-amber-500" />
          Charter seats claimed
        </div>
        <div className="text-sm text-amber-900">
          <span className="text-lg font-bold" data-testid="charter-counter-claimed">
            {fmt(claimed)}
          </span>
          <span className="text-amber-700"> / {fmt(cap)}</span>
          {is_soft_capped ? (
            <span className="ml-2 rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white">
              Cap reached — extended
            </span>
          ) : (
            <span className="ml-2 text-xs text-amber-700">{fmt(remaining)} left</span>
          )}
        </div>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-amber-100">
        <div
          className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-[width] duration-500"
          style={{ width: `${pctClamped}%` }}
          data-testid="charter-counter-bar"
        />
      </div>
    </div>
  );
};

export default CharterCounter;
